import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Grid, Card, CardContent,
  Avatar, Chip, Button, Divider, Alert, AlertTitle,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, Stack, Menu, MenuItem, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, CircularProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as AttendanceIcon,
  AttachMoney as FinanceIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Language as LanguageIcon,
  AccessTime as TimeIcon,
  LocationOn as RoomIcon,
  SwapHoriz as SwitchAcademyIcon,
  QrCodeScanner as QrScanIcon,
  StopCircle as StopIcon,
  DoneAll as DoneAllIcon,
  EventBusy as AbsenceIcon,
  AttachFile as AttachFileIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
  Teacher, AcademySession, AcademyAttendance, TeacherAbsenceRequest, PortalAcademy
} from '../../types/academy';
import { useTeacherPortalLanguage } from './contexts/TeacherPortalLanguageContext';
import { academyApi } from '../../services/academyApi';
import { blobticMessage } from '../../../Commun/components/BlobticMessage';

interface Props {
  token: string;
  teacher: Teacher;
  canTakeAttendance: boolean;
  academy: PortalAcademy;
  academiesCount: number;
  groups: any[];
  sessions: AcademySession[];
  absenceRequests: TeacherAbsenceRequest[];
  finances: {
    rate_type: string;
    hourly_rate: number;
    total_hours_month: number;
    total_amount_month: number;
    history: any[];
  };
  onSwitchAcademy: () => void;
  onLogout: () => void;
  onRefresh: () => Promise<void>;
}

export const TeacherPortalDashboard: React.FC<Props> = ({
  token,
  teacher,
  canTakeAttendance,
  academy,
  academiesCount,
  groups,
  sessions,
  absenceRequests,
  finances,
  onSwitchAcademy,
  onLogout,
  onRefresh,
}) => {
  const { t, language, setLanguage } = useTeacherPortalLanguage();

  const [currentTab, setCurrentTab] = useState<'overview' | 'attendance' | 'sessions' | 'groups' | 'finances' | 'absences'>('overview');
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  // Selected session for attendance
  const upcomingOrTodaySessions = sessions.filter(s => s.status !== 'CANCELLED');
  const [selectedSessionId, setSelectedSessionId] = useState<number>(upcomingOrTodaySessions[0]?.id || 0);
  const currentSession = sessions.find(s => s.id === selectedSessionId);

  // Session attendances
  const [sessionStudents, setSessionStudents] = useState<any[]>([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // Scanner state
  const [isScanning, setIsScanning] = useState(false);
  const [scanFeedback, setScanFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = 'teacher-portal-qr-reader';

  // Justification Dialog State
  const [justificationDialogOpen, setJustificationDialogOpen] = useState(false);
  const [targetAttendance, setTargetAttendance] = useState<any>(null);
  const [justificationReason, setJustificationReason] = useState('');
  const [justificationDocName, setJustificationDocName] = useState('');
  const [justificationDocBase64, setJustificationDocBase64] = useState('');

  // Absence Request Dialog State
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [targetSessionForAbsence, setTargetSessionForAbsence] = useState<AcademySession | null>(null);
  const [absenceStartDate, setAbsenceStartDate] = useState('');
  const [absenceEndDate, setAbsenceEndDate] = useState('');
  const [absenceReason, setAbsenceReason] = useState('');
  const [submittingAbsence, setSubmittingAbsence] = useState(false);
  const [absenceSuccessAlert, setAbsenceSuccessAlert] = useState(false);

  // Stop scanner on unmount or tab change
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const handleStartScanner = async () => {
    setScanFeedback(null);
    setIsScanning(true);

    setTimeout(async () => {
      try {
        const qrCode = new Html5Qrcode(scannerContainerId, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13,
          ],
          verbose: false,
        });
        html5QrCodeRef.current = qrCode;

        await qrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            await handleBadgeScanned(decodedText);
          },
          () => {}
        );
      } catch (err) {
        console.error('Failed to start camera scanner:', err);
        setScanFeedback({ type: 'error', message: 'Impossible d\'accéder à la caméra.' });
        setIsScanning(false);
      }
    }, 200);
  };

  const handleStopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch {}
    }
    setIsScanning(false);
  };

  const handleBadgeScanned = async (badgeCode: string) => {
    if (!selectedSessionId) return;
    try {
      const res = await academyApi.portalTeacherScanBadge(token, selectedSessionId, badgeCode);
      if (res && res.success) {
        const stdName = res.student_name || badgeCode;
        setScanFeedback({
          type: 'success',
          message: t('portal.teacher.scan_success_present', stdName)
        });
        await onRefresh();
      } else {
        setScanFeedback({
          type: 'error',
          message: res?.error || t('portal.teacher.scan_error')
        });
      }
    } catch {
      setScanFeedback({
        type: 'error',
        message: t('portal.teacher.scan_error')
      });
    }
  };

  const handleSaveAttendance = async (studentId: number, status: string) => {
    if (!selectedSessionId) return;
    try {
      await academyApi.portalTeacherSaveAttendance(token, selectedSessionId, studentId, status);
      await onRefresh();
    } catch (err) {
      console.error('Error saving attendance:', err);
    }
  };

  // Open Justification Dialog
  const handleOpenJustification = (att: any) => {
    setTargetAttendance(att);
    setJustificationReason(att.justification_reason || '');
    setJustificationDocName(att.justification_doc_name || '');
    setJustificationDocBase64(att.justification_doc_base64 || '');
    setJustificationDialogOpen(true);
  };

  const handleSaveJustification = async () => {
    if (!targetAttendance) return;
    try {
      await academyApi.portalTeacherSaveAbsenceJustification(
        token,
        targetAttendance.id || targetAttendance.ATD_IDF || 0,
        justificationReason,
        justificationDocName,
        justificationDocBase64,
        targetAttendance.session_id || targetAttendance.SSN_IDF || selectedSessionId,
        targetAttendance.student_id || targetAttendance.STD_IDF
      );
      setJustificationDialogOpen(false);
      await onRefresh();
    } catch (err) {
      console.error('Error saving justification:', err);
    }
  };

  // Submit Teacher Absence Request
  const handleOpenAbsenceDialog = (sess: AcademySession) => {
    setTargetSessionForAbsence(sess);
    setAbsenceStartDate(sess.date);
    setAbsenceEndDate(sess.date);
    setAbsenceReason('');
    setAbsenceDialogOpen(true);
  };

  const handleSubmitAbsenceRequest = async () => {
    if (!targetSessionForAbsence || !absenceReason.trim()) return;
    setSubmittingAbsence(true);
    try {
      const res = await academyApi.portalTeacherSubmitAbsenceRequest(
        token,
        targetSessionForAbsence.id,
        absenceStartDate,
        absenceEndDate,
        absenceReason
      );
      if (res && res.success) {
        setAbsenceDialogOpen(false);
        setAbsenceSuccessAlert(true);
        setTimeout(() => setAbsenceSuccessAlert(false), 6000);
        await onRefresh();
      } else {
        blobticMessage.error(t('portal.teacher.absence_error_title'), res?.message || t('portal.teacher.absence_error'));
      }
    } catch (err) {
      console.error('Failed to submit absence request:', err);
      blobticMessage.error(t('portal.teacher.absence_error_title'), t('portal.teacher.absence_error'));
    } finally {
      setSubmittingAbsence(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: (theme) => theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc' }}>
      {/* Top Navbar */}
      <Box
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.5,
          px: { xs: 2, md: 4 },
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={academy.logo_base64 || (academy as any).logo}
              variant="rounded"
              sx={{
                bgcolor: (academy.logo_base64 || (academy as any).logo) ? 'transparent' : 'secondary.main',
                background: (academy.logo_base64 || (academy as any).logo) ? 'transparent' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                width: 44,
                height: 44,
                borderRadius: 2,
                boxShadow: (academy.logo_base64 || (academy as any).logo) ? 'none' : '0 4px 10px rgba(139, 92, 246, 0.3)',
                border: (academy.logo_base64 || (academy as any).logo) ? '1px solid' : 'none',
                borderColor: 'divider',
                p: (academy.logo_base64 || (academy as any).logo) ? 0.5 : 0,
                '& img': {
                  objectFit: 'contain',
                  width: '100%',
                  height: '100%'
                }
              }}
            >
              <SchoolIcon />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
                {academy.dos_lib || academy.dos_code || t('portal.teacher.brand')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.2 }}>
                <Chip
                  label={teacher.specialization || 'Enseignant'}
                  size="small"
                  color="secondary"
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                />
                {academiesCount > 1 && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<SwitchAcademyIcon sx={{ fontSize: 14 }} />}
                    onClick={onSwitchAcademy}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0, px: 0.5 }}
                  >
                    {t('portal.teacher.change_academy')}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {/* User Controls & Language */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip title="Changer de langue / Change language">
              <IconButton onClick={(e) => setLangAnchor(e.currentTarget)} size="small">
                <LanguageIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={() => setLangAnchor(null)}
            >
              <MenuItem onClick={() => { setLanguage('fr'); setLangAnchor(null); }}>Français</MenuItem>
              <MenuItem onClick={() => { setLanguage('en'); setLangAnchor(null); }}>English</MenuItem>
              <MenuItem onClick={() => { setLanguage('ar'); setLangAnchor(null); }}>العربية</MenuItem>
            </Menu>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={teacher.photo_base64 || teacher.avatar_url}
                sx={{ width: 36, height: 36, bgcolor: 'secondary.main', fontSize: '0.9rem', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
              >
                {teacher.first_name?.[0] || 'T'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                  {teacher.first_name} {teacher.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {teacher.email || teacher.phone}
                </Typography>
              </Box>
            </Box>

            <Tooltip title={t('portal.teacher.logout')}>
              <IconButton onClick={onLogout} color="error" size="small">
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {absenceSuccessAlert && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            {t('portal.teacher.absence_success')}
          </Alert>
        )}

        {/* Dashboard Navigation Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <Tabs
            value={currentTab}
            onChange={(_e, val) => setCurrentTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="secondary"
            indicatorColor="secondary"
            sx={{ px: 2, pt: 1 }}
          >
            <Tab value="overview" icon={<SchoolIcon />} iconPosition="start" label={t('portal.teacher.tab_overview')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab
              value="attendance"
              icon={canTakeAttendance ? <AttendanceIcon /> : <LockIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={t('portal.teacher.tab_attendance')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab value="sessions" icon={<CalendarIcon />} iconPosition="start" label={t('portal.teacher.tab_sessions')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab value="groups" icon={<PersonIcon />} iconPosition="start" label={t('portal.teacher.tab_groups')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab value="finances" icon={<FinanceIcon />} iconPosition="start" label={t('portal.teacher.tab_finances')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab value="absences" icon={<AbsenceIcon />} iconPosition="start" label={t('portal.teacher.tab_absence_requests')} sx={{ textTransform: 'none', fontWeight: 600 }} />
          </Tabs>
        </Paper>

        {/* TAB 1: OVERVIEW */}
        {currentTab === 'overview' && (
          <Box>
            {/* Teacher Welcome Profile Banner */}
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 3,
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)'
                  : 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                border: '1px solid',
                borderColor: 'secondary.light',
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                flexWrap: 'wrap'
              }}
            >
              <Avatar
                src={teacher.photo_base64 || teacher.avatar_url}
                sx={{
                  width: 68,
                  height: 68,
                  bgcolor: 'secondary.main',
                  fontSize: '1.6rem',
                  fontWeight: 800,
                  boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
                  border: '2px solid white'
                }}
              >
                {teacher.first_name?.[0] || 'T'}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="h6" fontWeight={800} color="text.primary">
                  {t('portal.teacher.welcome', `${teacher.first_name} ${teacher.last_name}`.trim())}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={teacher.specialization || 'Enseignant'}
                    size="small"
                    color="secondary"
                    sx={{ fontWeight: 700, fontSize: '0.72rem', height: 22 }}
                  />
                  {teacher.email && (
                    <Typography variant="body2" color="text.secondary">
                      {teacher.email}
                    </Typography>
                  )}
                  {teacher.phone && (
                    <Typography variant="body2" color="text.secondary">
                      • {teacher.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
              {Boolean(academy.logo_base64 || (academy as any).logo) && (
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: 58,
                    minWidth: 70,
                    maxWidth: 140
                  }}
                >
                  <Box
                    component="img"
                    src={academy.logo_base64 || (academy as any).logo}
                    alt={academy.dos_lib || 'Logo'}
                    sx={{ maxHeight: 46, maxWidth: 120, objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Paper>

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '4px solid #8b5cf6', p: 1 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                      {t('portal.teacher.hours_month')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color="secondary.main" sx={{ my: 0.5 }}>
                      {finances?.total_hours_month || 0} h
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Taux unitaire : {finances?.hourly_rate || teacher.hourly_rate} DZD
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '4px solid #10b981', p: 1 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                      {t('portal.teacher.estimated_amount')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color="success.main" sx={{ my: 0.5 }}>
                      {(finances?.total_amount_month || 0).toLocaleString()} DZD
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Rémunération prévisionnelle
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '4px solid #3b82f6', p: 1 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                      {t('portal.teacher.tab_groups')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ my: 0.5 }}>
                      {groups.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Groupes assignés
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Next Sessions Preview */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Prochaines séances d'enseignement
            </Typography>
            <Grid container spacing={2}>
              {sessions.slice(0, 4).map(s => (
                <Grid item xs={12} sm={6} key={s.id}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 1 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {s.topic || s.group_name || 'Cours'}
                        </Typography>
                        <Chip label={s.status} size="small" color="secondary" variant="outlined" />
                      </Box>
                      <Stack spacing={0.8} sx={{ mt: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimeIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {s.date} • {s.start_time} - {s.end_time}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RoomIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {s.room_name || 'Salle assignée'} • Groupe : {s.group_name || '—'}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* TAB 2: ATTENDANCE (FEUILLE DE PRÉSENCE & SCAN BADGES) */}
        {currentTab === 'attendance' && (
          <Box>
            {!canTakeAttendance ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, borderColor: 'warning.light' }}>
                <LockIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Accès restreint
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                  {t('portal.teacher.attendance_permission_denied')}
                </Typography>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                {/* Session Selector & Scanner Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                  <Box sx={{ minWidth: 260 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                      {t('portal.teacher.active_session_label')}
                    </Typography>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={selectedSessionId}
                      onChange={(e) => setSelectedSessionId(Number(e.target.value))}
                    >
                      {sessions.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.date} ({s.start_time}-{s.end_time}) — {s.group_name || s.topic || `Séance #${s.id}`}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {isScanning ? (
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<StopIcon />}
                        onClick={handleStopScanner}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}
                      >
                        {t('portal.teacher.stop_scanner_btn')}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<QrScanIcon />}
                        onClick={handleStartScanner}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #8b5cf6, #6366f1)'
                        }}
                      >
                        {t('portal.teacher.scan_badge_btn')}
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Scanner Camera Viewer */}
                {isScanning && (
                  <Box
                    sx={{
                      p: 2,
                      mb: 3,
                      borderRadius: 3,
                      bgcolor: 'background.default',
                      border: '2px dashed',
                      borderColor: 'secondary.main',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body2" color="secondary.main" fontWeight={700} sx={{ mb: 2 }}>
                      {t('portal.teacher.scanner_instruction')}
                    </Typography>
                    <Box
                      id={scannerContainerId}
                      sx={{
                        maxWidth: 400,
                        mx: 'auto',
                        overflow: 'hidden',
                        borderRadius: 2,
                        minHeight: 280,
                        bgcolor: 'black'
                      }}
                    />
                  </Box>
                )}

                {scanFeedback && (
                  <Alert severity={scanFeedback.type} sx={{ mb: 2.5, borderRadius: 2 }}>
                    {scanFeedback.message}
                  </Alert>
                )}

                {/* Students Attendance Table */}
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                  Liste d'appel des étudiants
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('portal.teacher.student_col')}</strong></TableCell>
                        <TableCell><strong>{t('portal.teacher.status_col')}</strong></TableCell>
                        <TableCell><strong>{t('portal.teacher.arrival_col')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('portal.teacher.action_col')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!currentSession ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography color="text.secondary">{t('portal.teacher.select_session_prompt')}</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (() => {
                        const activeGroup = groups.find(g => (g.id || g.GRP_IDF) === currentSession?.group_id);
                        const groupStudents: any[] = activeGroup?.students || [];
                        const attendancesMap = (currentSession?.attendances || []).reduce((acc: any, a: any) => {
                          const sId = a.STD_IDF || a.std_idf || a.student_id;
                          if (sId) acc[sId] = a;
                          return acc;
                        }, {});

                        if (groupStudents.length === 0 && (currentSession.attendances || []).length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                <Typography color="text.secondary">{t('portal.teacher.no_students_enrolled')}</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        const listToRender = groupStudents.length > 0 ? groupStudents : (currentSession.attendances || []);

                        return listToRender.map((std: any) => {
                          const stdId = std.STD_IDF || std.std_idf || std.student_id || std.id;
                          const att = attendancesMap[stdId] || (std.ATD_STATUS ? std : null);
                          const atdStatus = (att?.ATD_STATUS || att?.status || '').toString();
                          const arrivalTime = att?.ATD_CHECK_IN_TIME
                            ? new Date(att.ATD_CHECK_IN_TIME).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : (att?.check_in_time || '—');
                          const studentName = std.STD_FULL_NAME || `${std.STD_FIRST_NAME || std.first_name || ''} ${std.STD_LAST_NAME || std.last_name || ''}`.trim() || `Élève #${stdId}`;
                          const studentCode = std.STD_CODE || std.code || `STD-${stdId}`;
                          const studentPhoto = std.STD_PHOTO_BASE64 || std.photo_base64;

                          const isPresent = atdStatus.toLowerCase() === 'present';
                          const isAbsent = atdStatus.toLowerCase() === 'absent';
                          const isLate = atdStatus.toLowerCase() === 'late';
                          const isExcused = atdStatus.toLowerCase() === 'excused';

                          return (
                            <TableRow key={stdId} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar
                                    src={studentPhoto}
                                    sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: '0.85rem' }}
                                  >
                                    {studentName[0] || 'E'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={700}>{studentName}</Typography>
                                    <Typography variant="caption" color="text.secondary">Code: {studentCode}</Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {isPresent ? (
                                  <Chip label={t('portal.teacher.mark_present')} size="small" color="success" />
                                ) : isAbsent ? (
                                  <Chip label={t('portal.teacher.mark_absent')} size="small" color="error" />
                                ) : isLate ? (
                                  <Chip label={t('portal.teacher.mark_late')} size="small" color="warning" />
                                ) : isExcused ? (
                                  <Chip label={t('portal.teacher.mark_excused')} size="small" color="info" />
                                ) : (
                                  <Chip label={t('portal.teacher.status_not_marked')} size="small" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell>{arrivalTime}</TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                                  <Button
                                    size="small"
                                    variant={isPresent ? "contained" : "outlined"}
                                    color="success"
                                    onClick={() => handleSaveAttendance(stdId, 'Present')}
                                    sx={{ textTransform: 'none', py: 0.2, minWidth: 70 }}
                                  >
                                    {t('portal.teacher.mark_present')}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant={isAbsent ? "contained" : "outlined"}
                                    color="error"
                                    onClick={() => handleSaveAttendance(stdId, 'Absent')}
                                    sx={{ textTransform: 'none', py: 0.2, minWidth: 70 }}
                                  >
                                    {t('portal.teacher.mark_absent')}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant={isLate ? "contained" : "outlined"}
                                    color="warning"
                                    onClick={() => handleSaveAttendance(stdId, 'Late')}
                                    sx={{ textTransform: 'none', py: 0.2, minWidth: 70 }}
                                  >
                                    {t('portal.teacher.mark_late')}
                                  </Button>
                                  {(isAbsent || isExcused) && (
                                    <Button
                                      size="small"
                                      variant="text"
                                      color="info"
                                      onClick={() => handleOpenJustification(att || { id: 0, student_id: stdId, session_id: selectedSessionId, student_name: studentName })}
                                      sx={{ textTransform: 'none', py: 0.2 }}
                                    >
                                      {t('portal.teacher.justify_absence')}
                                    </Button>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        )}

        {/* TAB 3: SESSIONS */}
        {currentTab === 'sessions' && (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              {t('portal.teacher.tab_sessions')}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Horaire</strong></TableCell>
                    <TableCell><strong>Groupe / Sujet</strong></TableCell>
                    <TableCell><strong>Salle</strong></TableCell>
                    <TableCell><strong>Statut</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map(s => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.date}</TableCell>
                      <TableCell>{s.start_time} - {s.end_time}</TableCell>
                      <TableCell><strong>{s.group_name || s.topic || '—'}</strong></TableCell>
                      <TableCell>{s.room_name || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={s.status}
                          size="small"
                          color={s.status === 'COMPLETED' ? 'success' : s.status === 'CANCELLED' ? 'error' : s.status === 'POSTPONED' ? 'warning' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<AbsenceIcon />}
                          onClick={() => handleOpenAbsenceDialog(s)}
                          sx={{ textTransform: 'none', borderRadius: 1.5 }}
                        >
                          Demander absence
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* TAB 4: GROUPS */}
        {currentTab === 'groups' && (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              {t('portal.teacher.tab_groups')}
            </Typography>
            <Grid container spacing={2}>
              {groups.map((g, idx) => (
                <Grid item xs={12} sm={6} key={g.id || idx}>
                  <Card variant="outlined" sx={{ borderRadius: 3, p: 1 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {g.name || g.GRP_NAME || `Groupe #${g.id || idx + 1}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('portal.teacher.group_students_count', g.students_count || g.max_capacity || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* TAB 5: FINANCES */}
        {currentTab === 'finances' && (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
              {t('portal.teacher.finances_title')}
            </Typography>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('portal.teacher.rate_type')}</Typography>
                <Typography variant="h6" fontWeight={700}>{finances?.rate_type || teacher.rate_type || 'Taux horaire'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('portal.teacher.hourly_rate')}</Typography>
                <Typography variant="h6" fontWeight={700} color="secondary.main">
                  {finances?.hourly_rate || teacher.hourly_rate} DZD
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('portal.teacher.hours_month')}</Typography>
                <Typography variant="h6" fontWeight={700}>{finances?.total_hours_month || 0} heures</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('portal.teacher.estimated_amount')}</Typography>
                <Typography variant="h5" fontWeight={800} color="success.main">
                  {(finances?.total_amount_month || 0).toLocaleString()} DZD
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* TAB 6: ABSENCE REQUESTS */}
        {currentTab === 'absences' && (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {t('portal.teacher.absence_requests_list')}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date demande</strong></TableCell>
                    <TableCell><strong>Période d'absence</strong></TableCell>
                    <TableCell><strong>Motif</strong></TableCell>
                    <TableCell><strong>Statut</strong></TableCell>
                    <TableCell><strong>Commentaire Admin</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {absenceRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">Aucune demande d'absence enregistrée.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    absenceRequests.map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.request_date || '—'}</TableCell>
                        <TableCell>{r.start_date} {r.end_date ? `au ${r.end_date}` : ''}</TableCell>
                        <TableCell>{r.reason}</TableCell>
                        <TableCell>
                          <Chip
                            label={r.status === 'APPROVED' ? t('portal.teacher.status_approved') : r.status === 'REJECTED' ? t('portal.teacher.status_rejected') : t('portal.teacher.status_pending')}
                            size="small"
                            color={r.status === 'APPROVED' ? 'success' : r.status === 'REJECTED' ? 'error' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>{r.admin_comment || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>

      {/* Absence Request Dialog */}
      <Dialog open={absenceDialogOpen} onClose={() => setAbsenceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('portal.teacher.absence_dialog_title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('portal.teacher.absence_session_label')}
              size="small"
              fullWidth
              disabled
              value={targetSessionForAbsence ? `${targetSessionForAbsence.date} (${targetSessionForAbsence.start_time}-${targetSessionForAbsence.end_time})` : ''}
            />
            <TextField
              label={t('portal.teacher.absence_start_date')}
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={absenceStartDate}
              onChange={(e) => setAbsenceStartDate(e.target.value)}
            />
            <TextField
              label={t('portal.teacher.absence_end_date')}
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={absenceEndDate}
              onChange={(e) => setAbsenceEndDate(e.target.value)}
            />
            <TextField
              required
              multiline
              rows={3}
              label={t('portal.teacher.absence_reason')}
              placeholder="Ex: Raison de santé, empêchement personnel..."
              value={absenceReason}
              onChange={(e) => setAbsenceReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAbsenceDialogOpen(false)}>{t('portal.teacher.cancel')}</Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!absenceReason.trim() || submittingAbsence}
            onClick={handleSubmitAbsenceRequest}
          >
            {submittingAbsence ? <CircularProgress size={20} /> : t('portal.teacher.absence_submit_btn')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Justification Dialog */}
      <Dialog open={justificationDialogOpen} onClose={() => setJustificationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('portal.teacher.justification_dialog_title', targetAttendance?.student_name || targetAttendance?.STD_FIRST_NAME || '')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              required
              multiline
              rows={3}
              label={t('portal.teacher.justification_reason_label')}
              placeholder="Ex: Certificat médical, raison familiale..."
              value={justificationReason}
              onChange={(e) => setJustificationReason(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setJustificationDialogOpen(false)}>{t('portal.teacher.cancel')}</Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!justificationReason.trim()}
            onClick={handleSaveJustification}
          >
            {t('portal.teacher.save_justification')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
